"use client";

import {
  ROUTE_MENU,
  ROUTE_MENU_ALL,
  ROUTE_SERVICES,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
} from "@/utils/constants";
import { useEffect } from "react";

interface CartDrawerMerchantFlags {
  isRetail?: boolean;
  isRestaurant?: boolean;
  isService?: boolean;
  isHybrid?: boolean;
  hasOnlyRetailItems?: boolean;
}

interface UseCartDrawerRedirectsParams {
  merchant: CartDrawerMerchantFlags;
  pathName: string | null;
  queryParams: string;
  serviceCartLength: number;
  retailCartLength: number;
  storedGiftCard: unknown;
  isRestaurantCartOpen: boolean;
  isAppointmentSummaryOpen: boolean;
  isRetailCartOpen: boolean;
  isHandlingCartDrawer: boolean;
  setIsHandlingCartDrawer: (value: boolean) => void;
  isProceedingToCheckout: boolean;
  replace: (href: string) => void;
  restaurantMfeEnabled?: boolean;
}

/**
 * Preserves the legacy drawer-to-landing-route redirects without adding their
 * merchant-specific branching to the NavigationBar render function.
 */
export const useCartDrawerRedirects = ({
  merchant,
  pathName,
  queryParams,
  serviceCartLength,
  retailCartLength,
  storedGiftCard,
  isRestaurantCartOpen,
  isAppointmentSummaryOpen,
  isRetailCartOpen,
  isHandlingCartDrawer,
  setIsHandlingCartDrawer,
  isProceedingToCheckout,
  replace,
  restaurantMfeEnabled = false,
}: UseCartDrawerRedirectsParams): void => {
  useEffect(() => {
    const restaurantMenuRoute = ROUTE_MENU(restaurantMfeEnabled);
    const restaurantMenuAllRoute = ROUTE_MENU_ALL(restaurantMfeEnabled);
    const shouldRedirectRestaurant =
      isHandlingCartDrawer && !isRestaurantCartOpen && merchant.isRestaurant;

    if (
      shouldRedirectRestaurant &&
      pathName != null &&
      pathName.startsWith(restaurantMenuRoute) &&
      pathName !== restaurantMenuAllRoute
    ) {
      globalThis.location.replace(`${restaurantMenuAllRoute}${queryParams}`);
    }
    if (
      shouldRedirectRestaurant &&
      pathName === restaurantMenuAllRoute
    ) {
      setIsHandlingCartDrawer(false);
    }
  }, [isRestaurantCartOpen, isHandlingCartDrawer, restaurantMfeEnabled]);

  useEffect(() => {
    const isReadyToHandle =
      isHandlingCartDrawer &&
      !isAppointmentSummaryOpen &&
      !isProceedingToCheckout;

    const shouldRedirectService =
      merchant.isService &&
      !merchant.isHybrid &&
      !merchant.hasOnlyRetailItems &&
      !storedGiftCard;

    const shouldRedirectHybridService =
      merchant.isHybrid &&
      (serviceCartLength > 0 || (!retailCartLength && !storedGiftCard));

    if (
      isReadyToHandle &&
      pathName != null &&
      pathName.startsWith(ROUTE_SERVICES) &&
      pathName !== ROUTE_SERVICES_ALL &&
      (shouldRedirectService || shouldRedirectHybridService)
    ) {
      replace(ROUTE_SERVICES_ALL);
    }
    if (isReadyToHandle && pathName === ROUTE_SERVICES_ALL) {
      setIsHandlingCartDrawer(false);
    }
  }, [isAppointmentSummaryOpen, isHandlingCartDrawer, isProceedingToCheckout]);

  useEffect(() => {
    const isReadyToHandle = isHandlingCartDrawer && !isRetailCartOpen;
    const isRetail = merchant.isRetail;
    const isHybridWithEmptyCarts =
      merchant.isHybrid && !retailCartLength && !serviceCartLength;

    if (isReadyToHandle && (isRetail || isHybridWithEmptyCarts)) {
      setIsHandlingCartDrawer(false);
    }

    const hasRetailItemsInCart = retailCartLength > 0 && !storedGiftCard;
    const shouldRedirectHybridRetail =
      merchant.isHybrid && hasRetailItemsInCart;
    const shouldRedirectServiceRetail =
      merchant.isService && merchant.hasOnlyRetailItems && hasRetailItemsInCart;

    if (
      isReadyToHandle &&
      pathName != null &&
      pathName.startsWith(ROUTE_SHOP) &&
      pathName !== ROUTE_SHOP &&
      (shouldRedirectHybridRetail || shouldRedirectServiceRetail)
    ) {
      replace(ROUTE_SHOP);
    }
    if (isReadyToHandle && pathName === ROUTE_SHOP) {
      setIsHandlingCartDrawer(false);
    }
  }, [isRetailCartOpen, isHandlingCartDrawer]);
};
